import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { getElementNodes } from "Course/modules/Utils";
import {
  isSubjectStandard,
  getEnableAllFieldsInCourseOverview,
  mypDisciplinaryFields,
  disciplinaryPlannerFields,
} from "./Utils";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  TEMPLATE_FIELD_LEARNING_STANDARDS_MYP,
  TEMPLATE_FIELD_RELATED_CONCEPTS_MYP,
  TEMPLATE_FIELD_OBJECTIVES_MYP,
} from "Constants/stringConstants";
import { getCourseOverviewDownloadQuery } from "./Queries";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  removeProgressElement,
  setProgress,
  downloadFromUrl,
} from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { generateRandomId, getArchivedSortedSubjectListMemoized } from "Utils";
import moment from "moment";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getLearningCourseVariables } from "LearningCourse/modules/Utils";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getIsCourseBasedGradingEnabled } from "LearningCourses/modules/Utils";
import { isParentTaggingEnabledForType } from "AppComponents/DatasetTaggingBody/modules/Utils";
import { MYP_OBJECTIVES } from "Teacher/routes/Projects/Constants/stringConstants";
import { getPropertyValue } from "Fields/FieldUtils";
import ACLStore from "lib/aclStore";

const shouldConvertNonPortedFieldsToPorted = () => {
  return ACLStore.can("FeatureFlag:EnablePortedFieldsForUnitTemplate");
};

export const NAME = "courseOverview";

export const UPDATE_FILTERS = "UPDATE_FILTERS" + " " + NAME;
export const SET_NO_COLUMN_SELECTED = "SET_NO_COLUMN_SELECTED" + " " + NAME;

export const updateFilters = data => {
  return { type: UPDATE_FILTERS, data: data };
};

export const setNoColumnSelected = data => {
  return { type: SET_NO_COLUMN_SELECTED, data };
};
const HEADER_DATA = "HEADER_DATA";
const BODY_DATA = "BODY_DATA";

// since remaining and covered element structure is different
// for PYP and rest of the curriculums
// this function extracts remaining and covered element nodes for both of them
const getRemainingAndCoveredElements = ({
  yearlyInsight,
  curriculumType,
  elementType,
}) => {
  // for PYP the structure is
  //  elementType
  //    elementsNotCovered
  //    elementsCovered
  if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
    const pypYearlyInsight = _.find(yearlyInsight, { type: elementType });
    return {
      // HACK: solve levelId conflicts by aliasing field in GQL
      remainingElement: _.has(pypYearlyInsight, "elementsNotCovered_")
        ? _.get(pypYearlyInsight, "elementsNotCovered_")
        : _.get(pypYearlyInsight, "elementsNotCovered"),
      coveredElement: _.get(pypYearlyInsight, "elementsCovered"),
    };
    // for other curriculums, its opposite
    //  elementSetsNotCovered
    //    elementType
    //  elementSetsCovered
    //    elementType
  } else {
    return {
      remainingElement: _.find(
        _.get(yearlyInsight, "elementSetsNotCovered", []),
        {
          type: elementType,
        }
      ),
      coveredElement: _.find(_.get(yearlyInsight, "elementSetsCovered", []), {
        type: elementType,
      }),
    };
  }
};

// this function returns element nodes based on curriculum type
// if the planner element is dependent on subject, it returns only that subject's nodes
const getPlannerElementNodes = ({
  elementSet,
  subject,
  curriculumType,
  plannerElementType,
  isInterdisciplinary = false,
}) => {
  const isParentTaggingEnabled = isParentTaggingEnabledForType({
    plannerElementType,
  });

  // for interdisciplinary and standards fields
  // filter out nodes subject wise
  if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
    const elementKey = isParentTaggingEnabled ? "nodes" : "elements";
    const elementNodes = _.get(elementSet, elementKey, []);

    return isInterdisciplinary
      ? _.filter(elementNodes, node => {
          const nodeSubject = node?.subject;
          return _.isEqual(nodeSubject, subject.id);
        })
      : elementNodes;
  } else {
    // need the whole plannerElementNode here instead of just id
    // because might need to further filter out based on isLeaf
    const nodesKey = isParentTaggingEnabled ? "nodes" : "plannerElementNode";
    return isInterdisciplinary
      ? getElementNodes({ elementSet, subject, isParentTaggingEnabled })
      : _.get(elementSet, nodesKey, []);
  }
};

/**
 * This function returns a custom object having relevant data for
 * insight of a Planner Element Data.
 *
 * @param {Object} param0
 * @param {Object} param0.itemObj - insight item object (from courseOverview array from template)
 * @param {Function} param0.valueFilter - element value filter function
 * @param {Array} param0.allPYPNodeIds - all node ids of pyp data
 * @param {String} param0.curriculumType - curriculum is pyp, myp, etc.
 * @param {Object} param0.yearlySchedule - yearly schedule of selected course
 */

const getInsightPlannerElementData = ({
  itemObj,
  fieldObj,
  unitPlan,
  enableAllFields,
  subjectIds,
  elementType,
  isNonPortedFieldsConvertedToPortedFields,
}) => {
  let valueCount = null;
  const fields = _.get(unitPlan, "fields", []);

  const fieldUID = _.get(itemObj, "fieldUID", "");

  let valueKeys = _.get(
    _.find(fields, field => _.get(field, "uid") === fieldUID),
    "value",
    []
  );

  let resolvedValue = _.get(
    _.find(fields, field => _.get(field, "uid") === fieldUID),
    "resolvedMinimalTree",
    {}
  );

  if (_.isEqual(elementType, "PYP_LEARNING_STANDARD")) {
    const benchmarks = _.get(resolvedValue, fieldUID, []);
    const filteredBenchmarks = _.filter(benchmarks, benchmark =>
      _.includes(subjectIds, _.get(benchmark, "subject"))
    );
    resolvedValue = { ...resolvedValue, benchmarks: filteredBenchmarks };
  }

  if (_.isEqual(elementType, "UBD_LEARNING_STANDARD")) {
    const subjectIdSet = new Set(subjectIds);
    const nodes = _.get(resolvedValue, "nodes", []);
    const filteredNodes = _.filter(nodes, node => {
      const subjects = _.get(
        node,
        "dataSetNodeWrapper.properties.subjects",
        []
      );
      return _.some(subjects, subject =>
        subjectIdSet.has(_.get(subject, "id"))
      );
    });
    const filteredNodeIdSet = new Set(_.map(filteredNodes, "id"));
    valueKeys = _.filter(valueKeys, id => filteredNodeIdSet.has(id));
    resolvedValue = { ...resolvedValue, nodes: filteredNodes };
  }

  // need to display as text for certain views
  if (_.includes(["GroupedConceptsView"], _.get(fieldObj, "viewType", ""))) {
    fieldObj["viewType"] = "TextView";
  }

  let uiType = "";

  if (isNonPortedFieldsConvertedToPortedFields) {
    uiType = getPropertyValue({
      propertyPath: "uiType",
      fieldObj,
    });
  }

  const viewType = _.isEmpty(uiType) ? _.get(fieldObj, "viewType", "") : uiType;
  const id = _.get(unitPlan, "id", "");
  valueKeys = valueKeys ? valueKeys : [];

  valueCount = _.size(valueKeys);

  return {
    viewType,
    valueCount,
    id,
    value: valueKeys,
    resolvedMinimalTree: resolvedValue,
    ...(enableAllFields && {
      type: _.get(fieldObj, "type", ""),
    }),
  };
};

/**
 * This function generates and returns the grid data unit wise for each subject.
 *
 * @param {Object} param0
 * @param {Object} param0.itemObj - insight item object (from courseOverview array from template)
 * @param {Array} param0.subjects - passed subject list
 * @param {Object} param0.unitPlan - passed unit plan
 * @param {String} param0.elementType - element type to be displayed
 * @param {Object} param0.yearlyInsight - myp insight of course
 * @param {Array} param0.yearlySchedule - yearly schedule of course
 */

const getInterdisciplinaryFieldsData = ({
  itemObj,
  fieldObj,
  subjects,
  unitPlan,
  elementType,
  yearlyInsight,
  curriculumType,
  tableDataType,
  canEdit,
  nodeFrequencyMap,
  isCourseBasedGradingEnabled,
  enableAllFields,
  isNonPortedFieldsConvertedToPortedFields,
  courseSubjects = [],
}) => {
  return _.map(subjects, subject => {
    const { remainingElement, coveredElement } = getRemainingAndCoveredElements(
      {
        elementType,
        curriculumType,
        yearlyInsight,
      }
    );

    let label = _.get(itemObj, "label", "");
    const subjectName = _.get(subject, "name", "");
    const subjectId = _.get(subject, "id", "");
    const isCourseSubject = _.find(courseSubjects, { id: subjectId });

    const unitPlanSubjects = _.get(unitPlan, "subjects.value", []);

    let fieldUID = _.get(itemObj, "fieldUID", "");
    let updatedItemObj = itemObj;

    if (_.has(itemObj, "interdisciplinaryFields")) {
      const interdisciplinaryFields = itemObj.interdisciplinaryFields;
      const interdisciplinaryUID = `${itemObj.interdisciplinaryUID}_${subjectId}`;

      if (
        _.includes(interdisciplinaryFields, interdisciplinaryUID) &&
        _.isEqual(tableDataType, BODY_DATA) &&
        _.isEqual(
          _.get(unitPlan, "unitType.value", ""),
          "ibMYPInterdisciplinary"
        )
      ) {
        fieldUID = interdisciplinaryUID;
        updatedItemObj = {
          ..._.cloneDeep(itemObj),
          fieldUID: interdisciplinaryUID,
        };
      }
    }

    const viewType = _.get(fieldObj, "viewType", "");

    // prefix subject name to disciplinary fields but we don't do that for DP
    if (
      (isSubjectStandard({ elementType }) ||
        _.includes(mypDisciplinaryFields, fieldUID)) &&
      !_.isEqual(elementType, "DP_SUBJECT_STANDARD") &&
      !isCourseBasedGradingEnabled
    )
      label = `${subjectName} ${label}`;
    let remainingElementNodes = [],
      allElementNodes = [];

    const isParentTaggingEnabled = isParentTaggingEnabledForType({
      plannerElementType: elementType,
    });

    const countKey = isParentTaggingEnabled ? "nodesCount" : "leafNodeCount";
    let remainingCount = _.get(remainingElement, countKey, 0),
      coveredCount = _.get(coveredElement, countKey, 0);

    if (_.isEqual(tableDataType, HEADER_DATA)) {
      remainingElementNodes = getPlannerElementNodes({
        elementSet: remainingElement,
        subject,
        curriculumType,
        plannerElementType: elementType,
        isInterdisciplinary: true,
      });

      const remainingElementLeafNodes = isParentTaggingEnabled
        ? remainingElementNodes
        : _.filter(remainingElementNodes, ["isLeaf", true]);

      const coveredElementNodes = getPlannerElementNodes({
        elementSet: coveredElement,
        subject,
        curriculumType,
        plannerElementType: elementType,
        isInterdisciplinary: true,
      });

      const coveredElementLeafNodes = isParentTaggingEnabled
        ? coveredElementNodes
        : _.filter(coveredElementNodes, ["isLeaf", true]);

      remainingCount = _.size(remainingElementLeafNodes) ?? remainingCount;
      coveredCount = _.size(coveredElementLeafNodes) ?? coveredCount;

      if (
        _.isEqual(curriculumType, CURRICULUM_TYPE_PYP) &&
        !isParentTaggingEnabled
      ) {
        // we only need count of remaining leaf nodes
        // concating remaining and covered since one of these sets must have the rootNode
        const rootNode = _.find(
          _.concat(remainingElementNodes, coveredElementNodes),
          item => !item.parent
        );

        const levels = rootNode?.levels ?? [];

        const remainingLeafNodes = _.filter(
          remainingElementNodes,
          item => item.levelId == levels[levels.length - 1]?.id || item.isLeaf
        );
        const coveredLeafNodes = _.filter(
          coveredElementNodes,
          item => item.levelId == levels[levels.length - 1]?.id || item.isLeaf
        );

        remainingCount = _.size(remainingLeafNodes);
        coveredCount = _.size(coveredLeafNodes);
      }
      remainingElementNodes = _.map(remainingElementNodes, "id");
      allElementNodes = _.uniq([
        ..._.map(coveredElementNodes, "id"),
        ...remainingElementNodes,
      ]);
    }

    const isSubjectMapped = _.includes(unitPlanSubjects, subjectId);

    const templateId = _.get(unitPlan, "templateId");

    const isMypObjective = _.isEqual(elementType, MYP_OBJECTIVES);

    let isMypObjectiveEditable = isMypObjective ? false : canEdit;

    if (isMypObjective) {
      const objectiveLevel = _.find(
        _.get(unitPlan, "fields", []),
        field => _.get(field, "uid") === "objectiveLevel"
      );

      const nodes = _.get(objectiveLevel, "resolvedMinimalTree.nodes", []);

      const hasSelectedPhase = _.some(nodes, node =>
        _.some(
          _.get(node, "dataSetNodeWrapper.properties.subjects", []),
          parent => _.isEqual(parent.id, subjectId)
        )
      );
      isMypObjectiveEditable = hasSelectedPhase;
    }

    switch (tableDataType) {
      case HEADER_DATA: {
        return {
          label,
          fieldUID,
          params: { subject: subjectName },
          type: elementType,
          coveredCount,
          remainingCount,
          viewType,
          subject,
          subjectId,
          fieldObj,
          remainingElementNodes,
          allElementNodes,
          hideCoveredLabel:
            isCourseSubject || _.isEqual(curriculumType, CURRICULUM_TYPE_PYP)
              ? _.get(itemObj, "hideCoveredLabel", false)
              : true,
          nodeFrequencyMap: _.get(nodeFrequencyMap, elementType),
        };
      }
      case BODY_DATA: {
        return {
          label,
          fieldUID,
          actualUID: _.get(itemObj, "fieldUID", ""),
          subjectId,
          subjectName,
          templateId,
          canEdit: canEdit && isSubjectMapped && isMypObjectiveEditable,
          disabledTooltip: isMypObjective
            ? "unitPlan:add_subject_groups_to_objectives"
            : isSubjectMapped
            ? "unitPlan:can_not_be_configured_for_unit_template"
            : "unitPlan:unit_not_mapped_to_subject_with_subject_name",
          params: { subject: subjectName },
          type: elementType,
          insightPlannerElement: getInsightPlannerElementData({
            itemObj: updatedItemObj,
            fieldObj,
            elementType,
            unitPlan,
            curriculumType,
            subjectIds: [subjectId],
            isCourseBasedGradingEnabled,
            enableAllFields,
            isNonPortedFieldsConvertedToPortedFields,
          }),
          viewType,
        };
      }
      default: {
        return {};
      }
    }
  });
};

/**
 * This function generates and returns the grid data Unit wise for planner elements
 * for elements mapped to subjectGroup.
 *
 * @param {Object} param0
 * @param {Object} param0.itemObj - insight item object (from courseOverview array from template)
 * @param {Array} param0.subjects - passed subject list
 * @param {Object} param0.unitPlan - passed unit plan
 * @param {String} param0.elementType - element type to be displayed
 * @param {Object} param0.yearlyInsight - myp insight of course
 * @param {Array} param0.yearlySchedule - yearly schedule of course
 */

const getOtherFieldsData = ({
  itemObj,
  fieldObj,
  subjects,
  elementType,
  unitPlan,
  yearlyInsight,
  curriculumType,
  tableDataType,
  canEdit,
  nodeFrequencyMap,
  isCourseBasedGradingEnabled,
  enableAllFields,
  isNonPortedFieldsConvertedToPortedFields,
}) => {
  const subjectIds = _.map(subjects, subject => subject.id);

  const { remainingElement, coveredElement } = getRemainingAndCoveredElements({
    elementType,
    curriculumType,
    yearlyInsight,
  });

  let params = {};
  const type = elementType;

  const fieldUID = _.get(itemObj, "fieldUID", "");
  let viewType = _.get(fieldObj, "viewType", "");

  if (isNonPortedFieldsConvertedToPortedFields) {
    viewType = getPropertyValue({
      propertyPath: "uiType",
      fieldObj,
    });
  }

  const subjectGroup = _.get(subjects, "[0]subjectGroup", "");
  params = { subjectGroup: _.get(subjectGroup, "name", "") };

  const countKey = isParentTaggingEnabledForType({
    plannerElementType: elementType,
  })
    ? "nodesCount"
    : "leafNodeCount";
  const remainingCount = _.get(remainingElement, countKey, 0);
  const coveredCount = _.get(coveredElement, countKey, 0);

  const label = itemObj.label;

  let remainingElementNodes = [],
    allElementNodes = [];

  if (_.isEqual(tableDataType, HEADER_DATA)) {
    remainingElementNodes = getPlannerElementNodes({
      curriculumType,
      plannerElementType: elementType,
      elementSet: remainingElement,
    });
    remainingElementNodes = _.map(remainingElementNodes, "id");
    const coveredElementNodes = _.map(
      getPlannerElementNodes({
        curriculumType,
        plannerElementType: elementType,
        elementSet: coveredElement,
      }),
      "id"
    );
    allElementNodes = _.uniq([
      ...coveredElementNodes,
      ...remainingElementNodes,
    ]);
  }

  const subjectId = _.first(subjects)?.id;

  const templateId = _.get(unitPlan, "templateId");

  switch (tableDataType) {
    case HEADER_DATA: {
      return {
        label,
        fieldUID,
        params,
        type,
        coveredCount,
        remainingCount,
        viewType,
        fieldObj,
        remainingElementNodes,
        allElementNodes,
        nodeFrequencyMap: _.get(nodeFrequencyMap, type),
        ...(enableAllFields && {
          hideCoveredLabel: _.get(itemObj, "hideCoveredLabel", false),
        }),
      };
    }
    case BODY_DATA: {
      return {
        label,
        fieldUID,
        params,
        type,
        templateId,
        canEdit,
        disabledTooltip: "unitPlan:can_not_be_configured_for_unit_template",
        subjectId,
        insightPlannerElement: getInsightPlannerElementData({
          itemObj,
          fieldObj,
          unitPlan,
          curriculumType,
          subjectIds,
          isCourseBasedGradingEnabled,
          enableAllFields,
          isNonPortedFieldsConvertedToPortedFields,
        }),
        viewType,
      };
    }
    default: {
      return {};
    }
  }
};

const getNodeFrequency = ({ yearlyInsightFrequency, curriculumType }) => {
  if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
    return _.reduce(
      yearlyInsightFrequency,
      (result, item) => {
        const plannerElementType = _.get(item, "type");
        const isParentTaggingEnabled = isParentTaggingEnabledForType({
          plannerElementType,
        });
        const transformed = _.reduce(
          item.elementsCoverageInsight,
          (acc, obj) => {
            acc[obj.element.id] = {
              count: isParentTaggingEnabled
                ? obj.directlyTaggedUnitPlansCount
                : obj.totalCount,
              unitPlans: isParentTaggingEnabled
                ? _.map(obj.directlyTaggedUnitPlans, ({ id }) => id)
                : _.map(obj.unitPlans, ({ id }) => id),
            };
            return acc;
          },
          {}
        );
        return { ...result, [item.type]: transformed };
      },
      {}
    );
  } else {
    const groupedByType = _.groupBy(yearlyInsightFrequency, "type");
    const transformedValues = _.mapValues(groupedByType, items => {
      const keyByPlannerElementNodeId = _.keyBy(items, "plannerElementNode.id");

      const plannerElementType = _.get(items, "[0].type");
      const isParentTaggingEnabled = isParentTaggingEnabledForType({
        plannerElementType,
      });

      return _.mapValues(keyByPlannerElementNodeId, obj => {
        return {
          count: isParentTaggingEnabled
            ? obj.directlyTaggedUnitPlansCount
            : obj.totalCount,
          unitPlans: isParentTaggingEnabled
            ? _.map(obj.directlyTaggedUnitPlans, ({ id }) => id)
            : _.map(obj.unitPlans, ({ id }) => id),
        };
      });
    });
    return transformedValues;
  }
};

/**
 * This function generates the Unit wise grid data structure using subjects, yearly insight and
 * yearly schedule. The data structure later on will be consumed by the local Grid Body
 * component .
 *
 * @param {Object} param0
 * @param {Array} param0.subjects - subjects of queried course
 * @param {String} param0.curriculumType - curriculum Type
 * @param {Object} param0.yearlyInsight - queried myp yearly insight contains element set covered
 * @param {Array} param0.yearlySchedule - yearly schedule of queried course
 */

const getData = ({
  subjects,
  yearlyInsight,
  curriculumType,
  tableDataType,
  unitPlan,
  courseOverviewFields,
  yearlyInsightFrequency = [],
  courseSubjects = [],
}) => {
  let plannerElementsData = [],
    nodeFrequencyMap = {};
  if (_.isEqual(tableDataType, HEADER_DATA)) {
    nodeFrequencyMap = getNodeFrequency({
      yearlyInsightFrequency,
      curriculumType,
    });
  }

  const enableAllFields = getEnableAllFieldsInCourseOverview();
  const isNonPortedFieldsConvertedToPortedFields = shouldConvertNonPortedFieldsToPorted();
  const isCourseBasedGradingEnabled = getIsCourseBasedGradingEnabled();

  _.forEach(courseOverviewFields, item => {
    // interdisciplinary fields are only displayed under their respective disciplinary field
    if (
      !_.get(item, "shouldDisplay", true) &&
      !_.includes(disciplinaryPlannerFields, item.fieldUID)
    )
      return;

    const fieldObj = _.isEqual(tableDataType, HEADER_DATA)
      ? _.get(item, "gradeFieldObj", {})
      : _.get(item, ["fieldObjects", _.get(unitPlan, "templateId")]);

    let elementType = _.get(
      fieldObj,
      "config.plannerElementConfig.type",
      _.get(item, "gradeFieldObj.config.plannerElementConfig.type", {})
    );

    if (isNonPortedFieldsConvertedToPortedFields) {
      elementType = getPropertyValue({
        propertyPath: "plannerElementConfigType",
        fieldObj,
      });
    }

    const itemObj = {
      ...item,
      elementType,
    };

    const canEdit = !_.isEmpty(fieldObj);

    if (
      (isSubjectStandard({ elementType }) ||
        _.has(item, "interdisciplinaryUID")) &&
      !isCourseBasedGradingEnabled
    ) {
      const interdisciplinaryFieldsData = getInterdisciplinaryFieldsData({
        itemObj,
        fieldObj,
        subjects,
        elementType,
        unitPlan,
        yearlyInsight,
        curriculumType,
        tableDataType,
        canEdit,
        nodeFrequencyMap,
        isCourseBasedGradingEnabled,
        enableAllFields,
        isNonPortedFieldsConvertedToPortedFields,
        courseSubjects,
      });
      plannerElementsData = [
        ...plannerElementsData,
        ...interdisciplinaryFieldsData,
      ];
    } else {
      let otherFieldsData = {};

      otherFieldsData = getOtherFieldsData({
        itemObj,
        fieldObj,
        subjects,
        elementType,
        unitPlan,
        yearlyInsight,
        curriculumType,
        tableDataType,
        canEdit,
        nodeFrequencyMap,
        isCourseBasedGradingEnabled,
        enableAllFields,
        isNonPortedFieldsConvertedToPortedFields,
      });

      if (!_.isEmpty(otherFieldsData))
        plannerElementsData = [...plannerElementsData, otherFieldsData];
    }
  });

  switch (tableDataType) {
    case HEADER_DATA: {
      return {
        plannerElementsHeaderData: plannerElementsData,
      };
    }
    case BODY_DATA: {
      return {
        unitPlan,
        plannerElementsData,
      };
    }
  }
};

const generateCourseOverviewGridData = ({
  subjects,
  yearlyInsight,
  yearlySchedule,
  curriculumType,
  courseOverviewFields,
  yearlyInsightFrequency,
  yearlyGroupByUnitPlans,
  isGroupByUnitType,
  courseSubjects,
}) => {
  const headerData = getData({
    subjects,
    yearlyInsight,
    curriculumType,
    courseOverviewFields,
    tableDataType: HEADER_DATA,
    yearlyInsightFrequency,
    courseSubjects,
  });

  let gridData = [headerData];
  const isDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);
  if (isGroupByUnitType && !isDP) {
    _.forEach(yearlyGroupByUnitPlans, group => {
      // Only push fieldValue if the node array is non-empty
      if (!_.isEmpty(group.node)) {
        gridData = [...gridData, { fieldValue: group.fieldValue }];

        _.forEach(group.node, nodeUnitPlan => {
          const bodyData = getData({
            subjects,
            yearlyInsight,
            curriculumType,
            unitPlan: nodeUnitPlan,
            courseOverviewFields,
            tableDataType: BODY_DATA,
          });

          gridData = [...gridData, bodyData];
        });
      }
    });
  } else {
    // Iterate over yearlySchedule and add to gridData
    _.forEach(yearlySchedule, unitPlan => {
      const bodyData = getData({
        subjects,
        yearlyInsight,
        curriculumType,
        unitPlan,
        courseOverviewFields,
        tableDataType: BODY_DATA,
      });

      gridData = [...gridData, bodyData];
    });
  }
  return gridData;
};

export const generateCourseOverviewGridDataMemoized = _.memoize(
  params => generateCourseOverviewGridData(params),
  params => JSON.stringify(params)
);

const getSubjectsByCurriculum = ({
  courseId,
  courseGradeFilters,
  curriculumType,
}) => {
  const enableAllFields = getEnableAllFieldsInCourseOverview();
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });
  if (enableAllFields || curriculumType === CURRICULUM_TYPE_PYP) {
    const primaryGradeId = _.get(courseDetails, "primaryGrade.id", "");
    const primaryGrade = _.find(_.get(courseDetails, "grades", []), {
      id: primaryGradeId,
    });
    return _.get(primaryGrade, "subjects", []);
  }
  return _.get(courseDetails, "subjects", []);
};

const getLearningCourseSubjectsByCurriculum = ({
  learningCourseId,
  academicYearId,
}) => {
  const learningCourseVariables = getLearningCourseVariables({
    learningCourseId,
    academicYearId,
  });
  const learningCourse = getLearningCourseDetailsFromCache(
    learningCourseVariables
  );
  return _.get(learningCourse, "subjects", []);
};

export const getCourseOverviewDownload = ({
  t,
  standardsSubjectIds = [],
  fieldUids = [],
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const curriculumProgramId = _.get(
      state,
      "platform.currentCurriculumProgram.id",
      null
    );

    const organizationId = _.get(state, "login.userInfo.org_id", "");
    const orgName = _.get(
      getOrganizationDetailsFromCache(organizationId),
      "name",
      ""
    );
    const courseId = _.get(state, "teacher.selected_class.selected_course", "");
    const academicYearId = getSelectedAcademicYearId(state);
    const courseGradeFilters = {
      academicYearId,
    };

    const courseTitle = _.get(
      getCourseDetailsFromCache({ id: courseId, courseGradeFilters }),
      "title",
      ""
    );

    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    );
    const grades = _.get(state, "teacher.selected_class.selected_grades", null);

    const curriculumSubjects = getSubjectsByCurriculumMemoized({
      courseId,
      curriculumType,
      courseGradeFilters,
    });

    const subjects = getArchivedSortedSubjectListMemoized({
      subjectList: curriculumSubjects,
    });

    const attachmentName = `${orgName} - ${courseTitle} - ${t(
      "common:course_overview"
    )} - ${moment().format("D MMMM YYYY")}`;

    const fileId = generateRandomId();

    const filters = {
      curriculumProgramId,
      academicYearId,
    };
    if (_.isEqual(curriculumType, CURRICULUM_TYPE_PYP)) {
      filters["subjects"] = _.map(subjects, subject => subject.id);
      filters["grades"] = grades;
    }

    if (!_.isEqual(curriculumType, CURRICULUM_TYPE_MYP)) {
      filters["standardsSubjectIds"] = standardsSubjectIds;
    } else {
      const fieldUidsWithSubjectSpecificFields = [
        ...fieldUids,
        TEMPLATE_FIELD_LEARNING_STANDARDS_MYP,
        TEMPLATE_FIELD_RELATED_CONCEPTS_MYP,
        TEMPLATE_FIELD_OBJECTIVES_MYP,
      ];
      filters["fieldUids"] = fieldUidsWithSubjectSpecificFields;
    }

    const variables = {
      id: courseId,
      filters,
    };

    try {
      const attachment = {
        id: fileId,
        type: "FILE",
        name: attachmentName,
        mimeType: "application/vnd.ms-excel",
      };
      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );
      const result = await client.query({
        query: getCourseOverviewDownloadQuery,
        variables,
        fetchPolicy: "network-only",
      });

      const planningInsightDownloadResult = _.get(
        result,
        ["data", "node", "courseOverviewDownload"],
        {}
      );

      if (_.get(planningInsightDownloadResult, "isSuccess", false)) {
        if (_.isNull(planningInsightDownloadResult.url)) {
          dispatch(removeProgressElement({ id: fileId }));
          dispatch(
            setToastMsg({
              msg: "toastMsgs:file_does_not_exist",
              type: "warning",
              position: "toast-top-center",
            })
          );
        } else {
          attachment.url = planningInsightDownloadResult.url;
          dispatch(downloadFromUrl({ attachment, shouldAskForSave: true }));
        }
      } else {
        dispatch(removeProgressElement({ id: fileId }));
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    } catch (e) {
      dispatch(removeProgressElement({ id: fileId }));

      if (e.response) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            type: "warning",
            position: "toast-top-center",
          })
        );
      } else if (e.networkError) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:no_internet_connection",
            type: "warning",
            position: "toast-top-center",
          })
        );
      }
    }
  };
};

export const getSubjectsByCurriculumMemoized = _.memoize(
  params => getSubjectsByCurriculum(params),
  props => JSON.stringify(props)
);

export const getLearningCourseSubjectsByCurriculumMemoized = _.memoize(
  params => getLearningCourseSubjectsByCurriculum(params),
  props => JSON.stringify(props)
);

const REDUCER_HANDLERS = {
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;

    _.forEach(_.keys(params), key => {
      state = update(state, {
        filters: {
          [key]: { $set: params[key] },
        },
      });
    });

    return state;
  },
  [SET_NO_COLUMN_SELECTED]: (state, action) => {
    return update(state, { noColumnSelected: { $set: action.data } });
  },
};

const initialState = {
  filters: { columnOptions: [], lastCourseId: null, fieldUIDs: [] },
  noColumnSelected: false,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
